import SCP_FRAMES_REQUEST_PRIORITY_TYPES from '../../consts/mw/scpFramesRequestPriorityTypes';
import helpers from '../../utils/helpers';
import {log} from '../log';

type TOnSuccess = (responses?: string[]) => void;
type TOnCancel = (success: boolean) => void;

interface IFramesQueueItem {
    frames: string[];
    onSuccess?: TOnSuccess;
    onError?: (isDeviceDisconnect?: boolean) => void;
    responses?: string[];
    timeout: number;
    skipErrorOnTimeout?: boolean;
    isDebug: boolean;
    responseTimeoutMs?: number;
    retryCount?: number;
    doNotSplit?: boolean;
    requestId?: string;
    priority?: SCP_FRAMES_REQUEST_PRIORITY_TYPES;
}

export interface IStateBase extends IFramesQueueItem {
    currentFrame?: string;
    retryNumber: number;
    wrongHeaderRetryCount: number;
    responses: string[];
}

export default abstract class CommunicatorClientBase<T, K extends IStateBase> {
    protected isActive = false;
    protected framesQueue: IFramesQueueItem[] = [];
    protected responseTimeout?: ReturnType<typeof setTimeout>;
    protected iqosClient!: T;
    protected state: K = this.getInitialState();
    protected requestIdToCancel?: string;
    protected onCancel?: TOnCancel;

    abstract getInitialState(): K;
    abstract writeFrames(): void;
    abstract writeFrame: (frame: string, isExtendedFrame: boolean) => void;

    addFramesToQueue({
        frames,
        onSuccess,
        onError,
        timeout,
        skipErrorOnTimeout,
        isDebug,
        responseTimeoutMs,
        retryCount,
        doNotSplit,
        requestId,
        priority,
    }: IFramesQueueItem): void {
        if (isDebug) {
            log.debug(
                `CommunicatorClientBase: add frames to queue, frames: ${JSON.stringify(frames)}, priority: ${priority}`
            );
        }

        const queueItem = {
            frames,
            onSuccess,
            onError,
            timeout,
            skipErrorOnTimeout,
            isDebug,
            responseTimeoutMs,
            retryCount,
            doNotSplit,
            requestId,
            priority,
        };

        this.framesQueue = this.sortQueue([queueItem, ...this.framesQueue]);
        this.processQueue();
    }

    restartListeners = (): void => undefined;

    cancelRequest(requestId: string, onCancel: TOnCancel): void {
        this.requestIdToCancel = requestId;
        this.onCancel = onCancel;

        // If queue is not empty or there is request in progress
        // cancellation will be called from writeFrames() method
        if (this.framesQueue.length) return;
        if (this.state.frames.length) return;

        this.callRequestCancellation();
        this.resetCancelationState();
    }

    protected resetRetries(): void {
        this.state.retryNumber = 0;
    }

    protected resetState(): void {
        this.state = this.getInitialState();
    }

    protected processQueue = (): void => {
        // If there is a request in progress -> do not do anything
        if (this.state.frames.length) return;

        this.processFrames();
    };

    protected processFrames = (): void => {
        this.resetState();

        if (!this.framesQueue.length) return;

        const request = this.framesQueue.shift()!;

        if (request.isDebug) {
            log.debug(`CommunicatorClientBase: process frames: ${JSON.stringify(request.frames)}`);
        }

        this.state = {
            ...this.state,
            ...request,
        };

        this.writeFrames();
    };

    protected onFrameResponseError = (): void => {
        const {onError} = this.state;

        this.clearResponseTimeout();
        onError?.();
        this.processFrames();
    };

    protected clearResponseTimeout(): void {
        clearTimeout(this.responseTimeout!);
    }

    protected onFramesResponse(): void {
        const {responses, onSuccess} = this.state;

        if (onSuccess) {
            const filteredResponses = this.filterEmptyResponses(responses);

            onSuccess(filteredResponses);
        }

        this.processFrames();
    }

    protected clearQueue(): void {
        this.state.onError?.(true);
        this.resetState();

        this.framesQueue.forEach((frameQueue) => {
            const {onError} = frameQueue;

            onError?.(true);
        });
        this.framesQueue = [];
    }

    protected stopRequestIfItIsCancelled(): boolean {
        const {requestIdToCancel} = this;

        // Cancelation is not needed
        if (!requestIdToCancel) return false;

        const {requestId, onSuccess, responses} = this.state;

        // If current request is the one that needs to be cancelled -> stop it and call onCancel
        if (requestId === requestIdToCancel) {
            log.debug(`CommunicatorClientBase: cancel current request, requestId: ${requestId}`);
            this.callRequestCancellation(onSuccess, responses);
            this.resetCancelationState();
            this.processFrames();

            return true;
        }

        const requestToCancel = this.framesQueue.find(({requestId}) => requestId === requestIdToCancel);

        // If frames queue contains request that needs to be cancelled -> remove it from the queue and call onCancel
        if (requestToCancel) {
            log.debug(`CommunicatorClientBase: cancel request, requestId: ${requestId}`);
            const {responses} = requestToCancel;

            this.callRequestCancellation(onSuccess, responses);
            this.removeCancelledRequestFromQueue();
            this.resetCancelationState();
            return false;
        }

        // There is no request in the queue that needs to be cancelled -> call onCancel
        this.callRequestCancellation();
        this.resetCancelationState();

        return false;
    }

    protected pauseRequestIfThereIsAnotherWithHigherPriority(): boolean {
        const {priority} = this.state;
        const isThereAnyRequestWithHigherPriority = this.framesQueue.some(
            (request) => (request.priority || 0) > (priority || 0)
        );

        if (!isThereAnyRequestWithHigherPriority) return false;

        log.debug(`CommunicatorClientBase: pause current request, there is another one with higher priority`);
        // Move current request after the requests with higher priority
        // but before other request with same priority
        this.framesQueue = this.sortQueue([{...this.state}, ...this.framesQueue]);
        this.processFrames();

        return true;
    }

    private sortQueue(queue: IFramesQueueItem[]): IFramesQueueItem[] {
        const result = queue.sort((a, b) => (b.priority || 0) - (a.priority || 0));

        return result;
    }

    private async callRequestCancellation(onSuccess?: TOnSuccess, responses?: string[]): Promise<void> {
        if (!this.onCancel) return;

        // Save onCancel because it will be cleaned
        const onCancel = this.onCancel;
        const filteredResponses = this.filterEmptyResponses(responses);

        log.debug(`CommunicatorClientBase: cancel request, responses: ${JSON.stringify(filteredResponses)}`);

        if (filteredResponses?.length) {
            onSuccess!(filteredResponses);
            const CM_WORKAROUND_DELAY_MS = 50;

            await helpers.timeout(CM_WORKAROUND_DELAY_MS);
        }

        const success = filteredResponses !== undefined;

        onCancel(success);
    }

    private resetCancelationState(): void {
        this.requestIdToCancel = undefined;
        this.onCancel = undefined;
    }

    private removeCancelledRequestFromQueue(): void {
        this.framesQueue = this.framesQueue.filter(({requestId}) => requestId !== this.requestIdToCancel);
    }

    private filterEmptyResponses(responses?: string[]): string[] | undefined {
        const result = responses?.filter((response) => response !== '');

        return result;
    }
}
